import toastify from 'components/Toastify'
import { PRIMARY_CURRENCY } from 'constants/currencies'
import { PERP_DEFAULT_MARKET, TRADE_DEFAULT_MARKET } from 'constants/trade'
import { useEffect } from 'react'
import { useLocation, useParams } from 'react-router-dom'
import { useAppDispatch } from 'store'
import { TradeType } from 'store/market/types'
import { updateCurrentPerpSymbol } from 'store/perp/actions'
import { updateCurrentSpotSymbol } from 'store/spot/actions'
import { TokenSymbol } from 'types'
import { CombinedSymbol, findSymbolByCurrency } from 'utils/symbols'
import { Equal } from '../utils/equal'
import { useMarketNavigate } from './useMarketNavigate'

// TRADE_DEFAULT_MARKET: defined in config/trade. e.g. "ETH-USD"
const defaultSpotMarket = {
  base: TRADE_DEFAULT_MARKET,
  quote: PRIMARY_CURRENCY,
}
const defaultPerpMarket = {
  base: PERP_DEFAULT_MARKET,
  quote: PRIMARY_CURRENCY,
}

export function findAvailableSymbol(
  symbols: Array<CombinedSymbol>,
  base: TokenSymbol,
  quote: TokenSymbol,
) {
  const symbol = findSymbolByCurrency(symbols, base, quote)
  return symbol ?? symbols[0]
}

export function useMatchUrlMarket(
  symbols: CombinedSymbol[],
  currentSymbol: Partial<CombinedSymbol> | undefined,
  tradeType: TradeType,
) {
  const dispatch = useAppDispatch()
  const match = useParams<{ base: TokenSymbol; quote: TokenSymbol }>()
  const location = useLocation()
  const defaultMarket =
    tradeType === TradeType.Perp ? defaultPerpMarket : defaultSpotMarket

  const { navigateToMarket } = useMarketNavigate()

  const updateCurrentSymbol = (symbol: CombinedSymbol) => {
    if (tradeType === TradeType.Perp) {
      dispatch(updateCurrentPerpSymbol(symbol.id))
    } else {
      dispatch(updateCurrentSpotSymbol(symbol.id))
    }
  }

  useEffect(() => {
    if (!symbols?.length) {
      return
    }
    const { baseCurrency, quoteCurrency } = currentSymbol ?? {}

    // If the URL is start with the "/restricted", redirect to default market.
    // This condition is always used SPOT market.
    if (location.pathname.startsWith('/restricted')) {
      const symbol = findAvailableSymbol(
        symbols,
        defaultMarket.base,
        defaultMarket.quote,
      )
      if (symbol) {
        updateCurrentSymbol(symbol)
      }
      return
    }

    // Redirect to default market if the current symbol is empty.
    if ((!baseCurrency || !quoteCurrency) && symbols?.length) {
      const base = match.base ?? defaultMarket.base
      const quote = match.quote ?? defaultMarket.quote
      const symbol = findAvailableSymbol(symbols, base, quote)
      if (symbol) {
        // Because the findAvailableSymbol function will return the first symbol if the default symbol is not found.
        navigateToMarket(tradeType, symbol.baseCurrency, symbol.quoteCurrency)
        updateCurrentSymbol(symbol)
      } else {
        toastify.error('Cannot find a available symbol. code: 1')
      }
      return
    }

    // Update the current symbol if the URL is changed.
    // E.g. visit the URL "/trade/ETH/USD" and the current symbol is "BTC_USD", then update the current symbol to "ETH_USD"
    if (
      baseCurrency &&
      quoteCurrency &&
      match.base &&
      match.quote &&
      !Equal.upperCase(baseCurrency, match.base)
    ) {
      // First, find the URL symbol is in the symbols list.
      let symbol = findSymbolByCurrency(symbols, match.base, match.quote)
      // If the symbol is not found, next find the default symbol.
      if (!symbol) {
        symbol = findAvailableSymbol(
          symbols,
          defaultMarket.base,
          defaultMarket.quote,
        )
      }
      // If the symbol is found, update the current symbol.
      // Otherwise, display the error message.
      if (symbol) {
        navigateToMarket(tradeType, symbol.baseCurrency, symbol.quoteCurrency)
        updateCurrentSymbol(symbol)
      } else {
        toastify.error('Cannot find a available symbol. code: 2')
      }
    }
  }, [currentSymbol, symbols, match])
}
